import { useState, useEffect, useCallback } from "react";
import { Settings, RotateCcw, Save, Type, Palette, Space, Eye, Minus, Plus } from "lucide-react";
import MarkdownRenderer from "../components/MarkdownRenderer";

/* ── Default theme values (must match :root in index.css) ── */
const DEFAULTS: Record<string, string> = {
  "--cx-body-size": "15px",
  "--cx-body-line-height": "1.7",
  "--cx-body-color": "#334155",
  "--cx-heading-color": "#0f172a",
  "--cx-heading-color-secondary": "#1e293b",
  "--cx-h1-size": "1.75rem",
  "--cx-h2-size": "1.375rem",
  "--cx-h3-size": "1.125rem",
  "--cx-h4-size": "0.9375rem",
  "--cx-h1-mt": "1.5rem",
  "--cx-h1-mb": "0.5rem",
  "--cx-h2-mt": "1.25rem",
  "--cx-h2-mb": "0.375rem",
  "--cx-h3-mt": "1rem",
  "--cx-h3-mb": "0.25rem",
  "--cx-h4-mt": "0.75rem",
  "--cx-h4-mb": "0.25rem",
  "--cx-p-mb": "0.625rem",
  "--cx-list-mt": "0.25rem",
  "--cx-list-mb": "0.625rem",
  "--cx-li-mb": "0.25rem",
  "--cx-block-margin": "0.75rem",
  "--cx-divider-margin": "1.25rem",
  "--cx-link-color": "#6366f1",
  "--cx-link-hover": "#4f46e5",
  "--cx-border-color": "#cbd5e0",
  "--cx-border-light": "#e2e8f0",
  "--cx-callout-border": "#818cf8",
  "--cx-callout-bg": "#f8fafc",
  "--cx-callout-color": "#475569",
  "--cx-code-bg": "#f1f5f9",
  "--cx-code-color": "#6d28d9",
  "--cx-code-border": "#e2e8f0",
  "--cx-codeblock-bg": "#fafbfc",
  "--cx-codeblock-header-bg": "#f1f5f9",
  "--cx-marker-color": "#94a3b8",
  "--cx-table-header-bg": "#f1f5f9",
  "--cx-table-hover-bg": "#f8fafc",
  "--cx-radius": "0.5rem",
  "--cx-radius-sm": "0.3rem",
};

const STORAGE_KEY = "cortex-theme";

type Section = {
  title: string;
  icon: React.ReactNode;
  items: { key: string; label: string; type: "size" | "color" | "number" }[];
};

const SECTIONS: Section[] = [
  {
    title: "Typography",
    icon: <Type className="size-4" />,
    items: [
      { key: "--cx-body-size", label: "Body font size", type: "size" },
      { key: "--cx-body-line-height", label: "Body line height", type: "number" },
      { key: "--cx-h1-size", label: "H1 size", type: "size" },
      { key: "--cx-h2-size", label: "H2 size", type: "size" },
      { key: "--cx-h3-size", label: "H3 size", type: "size" },
      { key: "--cx-h4-size", label: "H4 size", type: "size" },
      { key: "--cx-radius", label: "Border radius", type: "size" },
      { key: "--cx-radius-sm", label: "Small radius", type: "size" },
    ],
  },
  {
    title: "Spacing",
    icon: <Space className="size-4" />,
    items: [
      { key: "--cx-h1-mt", label: "H1 top margin", type: "size" },
      { key: "--cx-h1-mb", label: "H1 bottom margin", type: "size" },
      { key: "--cx-h2-mt", label: "H2 top margin", type: "size" },
      { key: "--cx-h2-mb", label: "H2 bottom margin", type: "size" },
      { key: "--cx-h3-mt", label: "H3 top margin", type: "size" },
      { key: "--cx-h3-mb", label: "H3 bottom margin", type: "size" },
      { key: "--cx-h4-mt", label: "H4 top margin", type: "size" },
      { key: "--cx-h4-mb", label: "H4 bottom margin", type: "size" },
      { key: "--cx-p-mb", label: "Paragraph bottom", type: "size" },
      { key: "--cx-list-mt", label: "List top margin", type: "size" },
      { key: "--cx-list-mb", label: "List bottom margin", type: "size" },
      { key: "--cx-li-mb", label: "List item bottom", type: "size" },
      { key: "--cx-block-margin", label: "Block margin (code, table, img)", type: "size" },
      { key: "--cx-divider-margin", label: "Divider margin", type: "size" },
    ],
  },
  {
    title: "Colors",
    icon: <Palette className="size-4" />,
    items: [
      { key: "--cx-body-color", label: "Body text", type: "color" },
      { key: "--cx-heading-color", label: "Heading primary", type: "color" },
      { key: "--cx-heading-color-secondary", label: "Heading secondary", type: "color" },
      { key: "--cx-link-color", label: "Link", type: "color" },
      { key: "--cx-link-hover", label: "Link hover", type: "color" },
      { key: "--cx-border-color", label: "Border", type: "color" },
      { key: "--cx-border-light", label: "Border light", type: "color" },
      { key: "--cx-callout-border", label: "Callout border", type: "color" },
      { key: "--cx-callout-bg", label: "Callout background", type: "color" },
      { key: "--cx-callout-color", label: "Callout text", type: "color" },
      { key: "--cx-code-bg", label: "Inline code bg", type: "color" },
      { key: "--cx-code-color", label: "Inline code text", type: "color" },
      { key: "--cx-code-border", label: "Inline code border", type: "color" },
      { key: "--cx-codeblock-bg", label: "Code block bg", type: "color" },
      { key: "--cx-codeblock-header-bg", label: "Code block header", type: "color" },
      { key: "--cx-marker-color", label: "List marker", type: "color" },
      { key: "--cx-table-header-bg", label: "Table header bg", type: "color" },
      { key: "--cx-table-hover-bg", label: "Table row hover", type: "color" },
    ],
  },
];

const PREVIEW_MD = `# Heading 1
## Heading 2
### Heading 3

This is body text with **bold**, *italic*, and \`inline code\`. Here's a [link](#).

- First item
- Second item
  - Nested item
- Third item

1. Ordered one
2. Ordered two

> This is a blockquote callout with some context.

\`\`\`javascript
function greet(name) {
  return \`Hello, \${name}\`;
}
\`\`\`

| Column A | Column B | Column C |
| --- | --- | --- |
| Cell 1 | Cell 2 | Cell 3 |
| Cell 4 | Cell 5 | Cell 6 |

---

That's the full theme preview.`;

/** Parse a CSS size value into numeric + unit */
function parseSize(val: string): { num: number; unit: string } {
  const match = val.match(/^(-?[\d.]+)\s*(rem|px|em|%)?$/);
  if (match) return { num: parseFloat(match[1]), unit: match[2] || "" };
  return { num: parseFloat(val) || 0, unit: "" };
}

/** Stepper input for size values (rem, px) with +/- buttons */
function SizeInput({ value, onChange, step: stepProp }: {
  value: string; onChange: (v: string) => void; step?: number;
}) {
  const { num, unit } = parseSize(value);
  const step = stepProp ?? (unit === "px" ? 1 : 0.125);

  const inc = () => onChange(`${Math.round((num + step) * 1000) / 1000}${unit}`);
  const dec = () => onChange(`${Math.max(0, Math.round((num - step) * 1000) / 1000)}${unit}`);

  return (
    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-white h-7">
      <button onClick={dec} className="w-7 h-full flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors border-r border-slate-200">
        <Minus className="size-3" strokeWidth={2} />
      </button>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-[64px] h-full px-1 text-[11px] font-mono text-slate-700 text-center focus:outline-none bg-transparent"
      />
      <button onClick={inc} className="w-7 h-full flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors border-l border-slate-200">
        <Plus className="size-3" strokeWidth={2} />
      </button>
    </div>
  );
}

/** Number input (no unit, e.g. line-height) with +/- */
function NumberInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const num = parseFloat(value) || 0;
  const step = 0.05;

  const inc = () => onChange(String(Math.round((num + step) * 1000) / 1000));
  const dec = () => onChange(String(Math.max(0, Math.round((num - step) * 1000) / 1000)));

  return (
    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-white h-7">
      <button onClick={dec} className="w-7 h-full flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors border-r border-slate-200">
        <Minus className="size-3" strokeWidth={2} />
      </button>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-[50px] h-full px-1 text-[11px] font-mono text-slate-700 text-center focus:outline-none bg-transparent"
      />
      <button onClick={inc} className="w-7 h-full flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors border-l border-slate-200">
        <Plus className="size-3" strokeWidth={2} />
      </button>
    </div>
  );
}

export default function SettingsPage() {
  const [tokens, setTokens] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULTS, ...JSON.parse(saved) } : { ...DEFAULTS };
    } catch { return { ...DEFAULTS }; }
  });
  const [dirty, setDirty] = useState(false);

  // Apply tokens to :root
  const applyTokens = useCallback((t: Record<string, string>) => {
    const root = document.documentElement;
    for (const [k, v] of Object.entries(t)) {
      root.style.setProperty(k, v);
    }
  }, []);

  useEffect(() => { applyTokens(tokens); }, [tokens, applyTokens]);

  const update = (key: string, value: string) => {
    setTokens(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    setDirty(false);
  };

  const reset = () => {
    setTokens({ ...DEFAULTS });
    localStorage.removeItem(STORAGE_KEY);
    setDirty(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Settings className="size-5 text-indigo-600" strokeWidth={1.75} />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Theme Settings</h1>
            <p className="text-xs text-slate-500">Customize typography, spacing, and colors across all rendered content</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
            <RotateCcw className="size-3" /> Reset defaults
          </button>
          <button onClick={save} disabled={!dirty}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors">
            <Save className="size-3" /> Save theme
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[380px_1fr] gap-6">
        {/* Controls */}
        <div className="space-y-4 max-h-[calc(100vh-180px)] overflow-y-auto pr-2">
          {SECTIONS.map(section => (
            <div key={section.title} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                <span className="text-indigo-600">{section.icon}</span>
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{section.title}</span>
              </div>
              <div className="divide-y divide-slate-50">
                {section.items.map(item => (
                  <div key={item.key} className="flex items-center justify-between px-4 py-2 gap-3">
                    <label className="text-[12px] text-slate-600 font-medium shrink-0">{item.label}</label>
                    <div className="flex items-center gap-1.5">
                      {item.type === "color" ? (
                        <div className="flex items-center gap-1.5">
                          <input type="color" value={tokens[item.key]}
                            onChange={e => update(item.key, e.target.value)}
                            className="size-6 rounded border border-slate-200 cursor-pointer p-0" />
                          <input type="text" value={tokens[item.key]}
                            onChange={e => update(item.key, e.target.value)}
                            className="w-[72px] px-1.5 py-0.5 text-[11px] font-mono border border-slate-200 rounded bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-300" />
                        </div>
                      ) : item.type === "number" ? (
                        <NumberInput value={tokens[item.key]} onChange={v => update(item.key, v)} />
                      ) : (
                        <SizeInput value={tokens[item.key]} onChange={v => update(item.key, v)} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Live preview */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
            <Eye className="size-4 text-indigo-600" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Live Preview</span>
          </div>
          <div className="px-6 py-5 max-h-[calc(100vh-180px)] overflow-y-auto">
            <MarkdownRenderer content={PREVIEW_MD} />
          </div>
        </div>
      </div>
    </div>
  );
}
